/**
 * How a refused write on Fonctionnalités or Règles reads (ADR-0016 §2.5).
 *
 * The console error carries a code (`validation_error`, `feature_not_enabled`,
 * `stale_version`…), the server's specific `reason` when it has one
 * (`effective_date_in_past`, `critical_requires_owner`…: ConsoleErrorReason,
 * ADR-0017 §6), an English message and the request ID. The screen switches on
 * the reason, and shows it in the staff member's language. A refusal with no
 * reason this knows falls back to the translation of its code: never the
 * English text.
 */
import { ConsoleApiError } from "../api/errors";
import { describeError } from "../auth/error-message";
import { t, type MessageId } from "../i18n";
import type { WriteFailure } from "./team/team-format";

export type RefusalKind =
  | "critical"
  | "scope_not_supported"
  | "scope_not_allowed"
  | "expiry_in_past"
  | "effective_date_in_past"
  | "legal_basis_required"
  | "invalid_value"
  | "target_not_found"
  | "rule_retired";

/** The server's `reason` for each refusal these screens have words for. */
const BY_REASON: Readonly<Record<string, RefusalKind>> = {
  critical_requires_owner: "critical",
  scope_not_supported: "scope_not_supported",
  scope_not_allowed: "scope_not_allowed",
  expiry_in_past: "expiry_in_past",
  effective_date_in_past: "effective_date_in_past",
  legal_basis_required: "legal_basis_required",
  invalid_value: "invalid_value",
  target_not_found: "target_not_found",
  rule_retired: "rule_retired",
};

/** The server's specific reason for a refusal, or null when it sent none these screens know. */
export function refusalKind(error: unknown): RefusalKind | null {
  if (!(error instanceof ConsoleApiError) || error.reason === null) return null;
  return BY_REASON[error.reason] ?? null;
}

/**
 * A failed write, in the staff member's language, with its request ID.
 * `messages` names the screen's own text for each reason it can meet;
 * `stale` its text for `stale_version`, which offers a reload.
 */
export function describeConfigFailure(
  error: unknown,
  messages: Partial<Record<RefusalKind, MessageId>>,
  stale: MessageId,
): WriteFailure {
  const { message, reference } = describeError(error);
  if (error instanceof ConsoleApiError && error.code === "stale_version") {
    return { message: t(stale), reference, stale: true };
  }
  const kind = refusalKind(error);
  const id = kind ? messages[kind] : undefined;
  return { message: id ? t(id) : message, reference, stale: false };
}
